import LCASHelpers from '@scripts/helpers';

/**
 * Form component
 */
export default class {
  /**
   * Component HTML element
   *
   * @private
   *
   * @type {HTMLFormElement}
   */
  #form;

  /**
   * @private
   *
   * @type {?AbortController}
   */
  #controller;

  /**
   * Submit button
   *
   * @type {?HTMLElement}
   */
  #button;

  /**
   * @param {HTMLFormElement} form Component HTML element
   */
  constructor(form) {
    this.#form = form;
    if (!this.#form) {
      throw new Error(
        'Licorn Alert Subscriber - Form: HTML element is required.'
      );
    }

    this.#button = this.#form.querySelector('[type="submit"]');

    this.#init();
  }

  /**
   * Init event listeners / components
   *
   * @private
   *
   * @return {void}
   */
  #init() {
    // listeners
    this.#form.addEventListener('submit', (e) => this.#handlerOnSubmit(e));
  }

  /**
   * Send an AJAX request after submitting the form
   *
   * @private
   *
   * @param {Event} e Submit event
   *
   * @return {void}
   */
  #handlerOnSubmit(e) {
    e.preventDefault();

    this.submit();
  }

  /**
   * Add a success alert to the form
   *
   * @private
   *
   * @param {string} message Success message
   *
   * @return {void}
   */
  #success(message) {
    this.reset();

    const success = document.createElement('p');
    success.classList.add('lcas-form-success', 'lcas-form-alert');
    success.innerText = message;

    this.#form.prepend(success);
  }

  /**
   * Add alert and error messages to the form
   *
   * @private
   *
   * @param {object} errors Error messages indexed by field name
   *  + the key '_global' create an alert
   *
   * @return {void}
   */
  #errors(errors) {
    /**
     * Create an HTML error message element
     *
     * @param {string} message Error message
     *
     * @return {HTMLElement}
     */
    const createError = (message) => {
      const err = document.createElement('p');
      err.classList.add('lcas-form-error');
      err.innerText = message;

      return err;
    };

    Object.keys(errors).forEach((name) => {
      if (name === '_global') {
        const err = createError(errors[name]);
        err.classList.add('lcas-form-alert');
        this.#form.prepend(err);
        return;
      }

      const input = this.#form.querySelector('[name="' + name + '"]');
      if (
        input &&
        input.parentNode &&
        input.parentNode.classList.contains('lcas-field')
      ) {
        const err = createError(errors[name]);
        input.parentNode.appendChild(err);
      }
    });
  }

  /**
   * Reset form
   *
   * @return {void}
   */
  reset() {
    this.#form.reset();
  }

  /**
   * Show form
   *
   * @return {void}
   */
  show() {
    this.#form.classList.add('active');
  }

  /**
   * Hide form
   *
   * @return {void}
   */
  hide() {
    this.#form.classList.remove('active');
  }

  /**
   * Get form element
   *
   * @return {HTMLFormElement}
   */
  get element() {
    return this.#form;
  }

  /**
   * Send a subscription or unsubscription request
   *
   * @return {Promise}
   */
  async submit() {
    // prevent multiple AJAX request
    if (this.#controller) {
      this.#controller.abort();
    }
    this.#controller = new AbortController();
    const signal = this.#controller.signal;

    // request data
    const reqData = new FormData(this.#form);

    // remove messages
    this.#form
      .querySelectorAll('.lcas-form-error, .lcas-form-success')
      .forEach((elt) => elt.remove());

    // lock submit
    if (this.#button) {
      this.#button.disabled = true;
    }

    let res = false;

    try {
      let { success, errors, message } = await LCASHelpers.sendRequest(
        licornAlertsub.ajaxUrl,
        {
          method: 'POST',
          body: reqData,
          signal,
        }
      );

      if (!success) {
        if (errors.length <= 0 && message) {
          // global error
          this.#errors({ _global: message });
        } else {
          // error by field
          this.#errors(errors);
        }
      } else {
        this.#success(message);
      }

      res = success;
    } catch {
      this.#errors({ _global: 'Technical Error: please try again later.' });
    } finally {
      this.#controller = null;

      // unlock submit
      if (this.#button) {
        this.#button.disabled = false;
      }
    }

    return res;
  }
}
